import React, {createContext, useContext, useMemo} from 'react';
import {deriveRole} from '../utils/ticketPerms';

// "loading" is retained in the union so existing `status === "loading"` checks
// (AppNav) stay type-valid; on the integrated path it is never produced.
type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface UserProfile {
	id: string;
	name: string;
	email: string;
	role: 'agent' | 'lead' | 'admin';
	/** Granular Forecast ticket permissions (ACC-436). Source of truth; `role`
	 *  is derived from these for the legacy hasMinRole gates. */
	ticketPerms: string[];
	/** Whether this person may invoke AI features: their profile grants AI_ACCESS and the
	 *  company has AI on. Computed by the Forecast shell, which owns both facts. */
	aiAllowed?: boolean;
}

/**
 * Raw identity supplied by the Forecast shell (web-front `ticketing-embed.tsx`)
 * on the integrated path, or `null` in standalone dev. The client derives the
 * coarse `role` from `ticketPerms` rather than trusting a pre-computed role.
 */
export interface ForecastIdentity {
	id: string;
	name: string;
	email: string;
	ticketPerms: string[];
	/** Absent means not allowed. */
	aiAllowed?: boolean;
}

interface AuthState {
	status: AuthStatus;
	user: UserProfile | null;
	logout: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({identity, children}: {identity: ForecastIdentity | null; children: React.ReactNode}) {
	const user = useMemo<UserProfile | null>(() => {
		if (!identity) return null;
		return {
			id: identity.id,
			name: identity.name,
			email: identity.email,
			role: deriveRole(identity.ticketPerms),
			ticketPerms: identity.ticketPerms,
			aiAllowed: identity.aiAllowed,
		};
	}, [identity]);

	const status: AuthStatus = user ? 'authenticated' : 'unauthenticated';

	function logout() {
		// Integrated path: logout redirects to the Forecast app root (host owns session).
		window.location.assign('/');
	}

	return <AuthContext.Provider value={{status, user, logout}}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error('useAuth must be used within AuthProvider');
	return ctx;
}

/**
 * Whether the signed-in person may invoke AI (draft a reply, regenerate a summary). Fails closed
 * when there is a provider but no user. Outside any provider, as in focused component tests, it is
 * allowed, the same way EmbeddedContext gives components a default outside its provider.
 */
export function useAiAllowed(): boolean {
	const ctx = useContext(AuthContext);
	if (!ctx) return true;
	return ctx.user?.aiAllowed === true;
}
